import React, { useState, useEffect } from 'react';
import {
  Box, Modal, Typography, IconButton, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import { FolderData } from '../../types/types';
import { updateFolder } from '../../api/folderAPI';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
  folders: FolderData[];
  onCreate: (folderName: string, parentFolderId?: string | null) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (folder: string) => void;
}

const ModalFolders: React.FC<Props> = ({ open, onClose, folders, onCreate, onRename, onDelete }) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newFolderNameCreate, setNewFolderNameCreate] = useState<string>('');

  //subfolder
  const [parentFolderIdForSub, setParentFolderIdForSub] = useState<string | null>(null);
  const [newSubfolderName, setNewSubfolderName] = useState<string>('');

  const { t, i18n } = useTranslation();
    
  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  const handleOpenCreate = () => {
    setNewFolderNameCreate('');
    setCreateModalOpen(true);
  };

  const handleConfirmCreate = () => {
    if (newFolderNameCreate.trim() !== '') {
      onCreate(newFolderNameCreate.trim(), null);
    }
    setCreateModalOpen(false);
  };


  const handleEdit = (folder: FolderData) => {
    setFolderToEdit(folder.folderId);
    setNewFolderName(folder.folderName);
    setEditModalOpen(true);
  };

  const handleConfirmRename = async () => {
    if (!folderToEdit || newFolderName.trim() === '') return;

    try {
      await updateFolder(folderToEdit, newFolderName.trim(), process.env.REACT_APP_COMPANY_ID!);
      onRename(folderToEdit, newFolderName.trim());
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error renaming folder:', error);
      alert('Failed to rename folder.');
    }
  };

  //subfolder
  const handleConfirmCreateSubfolder = () => {
    if (!parentFolderIdForSub || newSubfolderName.trim() === '') return;
    onCreate(`${parentFolderIdForSub}::${newSubfolderName.trim()}`);
    setParentFolderIdForSub(null);
    setNewSubfolderName('');
  };

  const renderFolderList = (folders: FolderData[], level = 0) => {
    return folders.map((folder) => (
      <Box key={folder.folderId} sx={{ ml: level * 2 }}>
        <ListItem divider>
          <ListItemText primary={folder.folderName} />
          <ListItemSecondaryAction>
            <IconButton
              onClick={() => {
                setParentFolderIdForSub(folder.folderId);
                setNewSubfolderName('');
              }}
            >
              <AddIcon />
            </IconButton>
            <IconButton onClick={() => handleEdit(folder)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(folder.folderId)}>
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>

        {/* Input para subfolder si se ha pulsado el "+" */}
        {parentFolderIdForSub === folder.folderId && (
          <Box sx={{ pl: 4, display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <TextField
              size="small"
              value={newSubfolderName}
              onChange={(e) => setNewSubfolderName(e.target.value)}
              label={t('subfolderName')}
              sx={{
                flex: 1,
                '& label.Mui-focused': {
                  color: '#A5D6A7',
                },
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#A5D6A7',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleConfirmCreateSubfolder}
              sx={{ backgroundColor: '#73AF73' }}
            >
              {t('create')}
            </Button>
          </Box>
        )}

        {/* Subfolders recursivos */}
        {folder.subfolders && folder.subfolders.length > 0 && (
          <Box>
            {renderFolderList(folder.subfolders, level + 1)}
          </Box>
        )}
      </Box>
    ));
  };

  return (
    <>
      {/* Main modal */}
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            maxHeight: '50vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <FolderIcon sx={{ marginRight: 1, color: '#73AF73' }} />
              <Typography variant="h6" sx={{ color: '#73AF73' }}>{t('folders')}</Typography>
            </Box>

            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ marginRight: 1, backgroundColor: '#73AF73' }}
            >
              {t('newFolder')}
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <List>
              {renderFolderList(folders, 0)}
            </List>
          </Box>
        </Box>
      </Modal>
      {/* Modal to create a folder */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FolderIcon sx={{ color: '#73AF73', mr: 1, mb:2 }} />
            <Typography variant="h6" sx={{ color: '#73AF73', mb:2 }}>
              {t('newFolder')}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={newFolderNameCreate}
            onChange={(e) => setNewFolderNameCreate(e.target.value)}
            label={t('folderName')}
            sx={{
              marginBottom: 2,
              '& label.Mui-focused': {
                color: '#A5D6A7',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#A5D6A7',
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setCreateModalOpen(false)}
              sx={{
                color: '#d32f2f',
                borderColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#fddede', 
                  borderColor: '#d32f2f',
                },
              }}
            >
              {t('cancel')}
            </Button>
            <Button variant="contained" onClick={handleConfirmCreate} sx={{ backgroundColor: '#73AF73' }}>{t('create')}</Button>
          </Box>
        </Box>
      </Modal>
      {/* Modal to edit the name */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FolderIcon sx={{ color: '#73AF73', mr: 1, mb:3 }} />
            <Typography variant="h6" sx={{ color: '#73AF73', mb:3 }}>
              {t('renameFolder')}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            label={t('newName')}
            sx={{
              marginBottom: 2,
              '& label.Mui-focused': {
                color: '#A5D6A7',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#A5D6A7',
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setEditModalOpen(false)}
              sx={{
                color: '#d32f2f',
                borderColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#fddede', 
                  borderColor: '#d32f2f',
                },
              }}
            >
              {t('cancel')}
            </Button>
            <Button variant="contained" onClick={handleConfirmRename} sx={{ backgroundColor: '#73AF73' }}>{t('save')}</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ModalFolders;